import { useEffect, useState } from "react";

interface SafeImageProps {
  /** Real image URL coming from the backend. May be empty/missing. */
  src?: string;
  /** Relevant photorealistic image shown when `src` is empty or fails to load. */
  fallback: string;
  alt: string;
  title?: string;
  className?: string;
}

/**
 * Image that never renders broken or blank.
 *
 * - If `src` is empty/missing → shows the `fallback` image.
 * - If `src` fails to load (404, dead URL) → swaps to the `fallback` image.
 *
 * Use this everywhere a backend-driven image is displayed so cards,
 * banners and galleries always look intentional.
 */
export default function SafeImage({ src, fallback, alt, title, className }: SafeImageProps) {
  const initial = src && src.trim() ? src : fallback;
  const [current, setCurrent] = useState(initial);

  // Re-sync when the source or the fallback changes (e.g. list re-fetch).
  useEffect(() => {
    setCurrent(src && src.trim() ? src : fallback);
  }, [src, fallback]);

  return (
    <img
      src={current}
      alt={alt}
      title={title}
      className={className}
      onError={() => {
        if (current !== fallback) setCurrent(fallback);
      }}
    />
  );
}